import debounce from 'lodash/debounce'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import { arrayToObject, EmptyArray, groupBy, sortBy, sum, without } from 'lib/array/arrayUtils'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import Group from 'components/utils/Group'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'

interface CategoryItem {
  category: Omit<App.ExperienceItemCategory, 'name'> & { name: string | React.ReactNode }
  count: number
  items: Array<FilterPanelCheckItem>
  isLuxPlus?: boolean
}

const GIFT_INSPIRATION_CATEGORY_ID = 56
/**
 * Returns the display priority for a category filter.
 * Lower values appear first in the filter list.
 *
 * Priority order:
 * 1. LuxPlus+ (priority 0) - Premium membership category always at top
 * 2. Gift Inspiration (priority 1) - Gift Inspiration category second
 * 3. All other categories (priority 2) - Maintain original order
 */
function getCategoryDisplayPriority(item: CategoryItem): number {
  if (item.isLuxPlus) {
    return 0
  }
  if (item.category.id === GIFT_INSPIRATION_CATEGORY_ID) {
    return 1
  }
  return 2
}

interface Props {
  place?: App.Place
  categories: Array<App.ExperienceItemCategory>
  isLuxPlusEnabled: boolean
}

function ExperienceSearchPageFilters({ place, categories = EmptyArray, isLuxPlusEnabled }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const {
    searchFilters,
    setPriceGteSearchFilter,
    setPriceLteSearchFilter,
    setSearchFilters,
  } = useExperiencesSearchFilters()

  const { categories: categoriesFacets, collections, priceRange, fetching } = useMostPopulatedSearchExperienceFacets(place)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  const onCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const category = categories.find(cat => cat.id === parseInt(e.currentTarget.value))

    if (category) {
      const selectedCategories = searchFilters.categoryCodes ?? []
      let nextLuxPlusFeatures = searchFilters.luxPlusFeatures

      let nextCategories = [...selectedCategories]
      if (!category.parentId || selectedCategories.includes(category.parentId)) {
        // parentID selected - at the moment, all items for the parent are selected
        // in this instance, we toggle the parent and this category off but ensure all others remain
        const otherChildCategories = categories.filter(cat => cat.parentId === category.parentId && cat.id !== category.id)
        nextCategories = otherChildCategories.map(cat => cat.id)
      } else if (selectedCategories.includes(category.id)) {
        // toggling off a single category
        nextCategories = without(nextCategories, category.id)
      } else {
        // toggling on a single category
        if (category.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID) {
          if (nextLuxPlusFeatures?.includes('Member prices')) {
            nextLuxPlusFeatures = undefined
          } else {
            nextLuxPlusFeatures = ['Member prices']
          }
        } else {
          nextCategories.push(category.id)
        }
      }

      setSearchFilters({
        ...searchFilters,
        categoryCodes: nextCategories,
        luxPlusFeatures: nextLuxPlusFeatures,
      })
    }
  }, [categories, searchFilters, setSearchFilters])

  const categoryItems = useMemo((): Array<CategoryItem> => {
    const childCategoriesByParent = groupBy(
      categories,
      cat => cat.parentId,
    )
    const facetsById = arrayToObject(categoriesFacets, facet => facet.categoryId)
    const topLevelCategories = categories.filter(cat => cat.level === 'parent')
    const selectedCategories = searchFilters.categoryCodes ?? []

    return topLevelCategories.map((category) => {
      const isLuxPlus = category.id === EXP_CATEGORY_LUX_PLUS_ID
      const childCategories = sortBy(childCategoriesByParent.get(category.id) ?? [], cat => facetsById[cat.id]?.count ?? 0, 'desc')
      const total = sum(childCategories, cat => facetsById[cat.id]?.count ?? 0)

      return {
        category: {
          ...category,
          name: isLuxPlus ? <LuxPlusLabel type="only-logo" /> : category.name,
        },
        count: total,
        items: childCategories.map((category): FilterPanelCheckItem => {
          const count = facetsById[category.id]?.count ?? 0

          const checked = isLuxPlus ? !!searchFilters.luxPlusFeatures?.length : selectedCategories.includes(category.id) || selectedCategories.includes(category.parentId ?? -1)

          return {
            label: <Group direction="horizontal" horizontalAlign="space-between">
              {category.name}
              <span>{intl.formatMessage({
                id: 'Al2DAPmSBe4qghlI9zF-',
                defaultMessage: '({count})',
                description: 'Category filter count in parentheses on experience search page',
              }, { count })}
              </span>
            </Group>,
            hidden: count === 0,
            value: category.id,
            checked,
          }
        }),
        isLuxPlus,
      }
    })
      .filter(val => val.count > 0 && (!val.isLuxPlus || isLuxPlusEnabled))
      .sort((a, b) => getCategoryDisplayPriority(a) - getCategoryDisplayPriority(b))
  }, [categories, categoriesFacets, searchFilters.categoryCodes, isLuxPlusEnabled, searchFilters.luxPlusFeatures, intl])

  const onCollectionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const collection = collections.find(col => col.collectionId === e.currentTarget.value)
    if (collection) {
      const selectedCollections = searchFilters.collections ?? []
      let nextCollections = [...selectedCollections]
      if (selectedCollections.includes(collection.collectionId)) {
        nextCollections = without(nextCollections, collection.collectionId)
      } else {
        nextCollections.push(collection.collectionId)
      }
      setSearchFilters({
        ...searchFilters,
        collections: nextCollections,
      })
    }
  }, [collections, searchFilters, setSearchFilters])

  const collectionItems = useMemo(() => {
    return collections.map((collection): FilterPanelCheckItem => {
      const checked = searchFilters.collections?.includes(collection.collectionId)
      return {
        label: <Group direction="horizontal" horizontalAlign="space-between">
          {collection.theme}
        </Group>,
        hidden: collection.count === 0,
        value: collection.collectionId,
        checked,
      }
    }).filter(item => !item.hidden)
  }, [collections, searchFilters.collections])

  const slider = useMemo(() => {
    if (priceRange) {
      const { min: pmin, max: pmax } = priceRange
      const step = pmax <= 10000 ? 10 : 100
      const min = Math.floor(pmin / step) * step
      const max = Math.ceil(pmax / step) * step
      return { min, max, step }
    }
  }, [priceRange])

  const onResetCategory = useCallback((item: CategoryItem) => {
    const selectedCategories = searchFilters.categoryCodes ?? []
    const nextCategories = without(selectedCategories, item.category.id, ...item.items.map(i => i.value as number))
    setSearchFilters({
      ...searchFilters,
      categoryCodes: nextCategories,
      luxPlusFeatures: undefined,
    })
  }, [searchFilters, setSearchFilters])

  const onResetCollection = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      collections: undefined,
    })
  }, [searchFilters, setSearchFilters])

  const onPriceGteChange = useMemo(() => debounce<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPriceGteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceGteSearchFilter])

  const onPriceLteChange = useMemo(() => debounce<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPriceLteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceLteSearchFilter])

  return <VerticalSpacer gap={24}>
    {fetching && <FilterPanelLoader type="checkbox" />}
    <FilterPanelFilterGroup
      title={intl.formatMessage({
        id: 'uhzheYPeu42wS_U04j7E',
        defaultMessage: 'Price',
        description: 'Title for the price filter group in experience search filters',
      })}
    >
      { slider && <DualSliderInput
        format="currency"
        min={slider.min}
        max={slider.max}
        defaultStartValue={searchFilters.priceGte}
        defaultEndValue={searchFilters.priceLte}
        step={slider.step}
        onStartChange={onPriceGteChange}
        onEndChange={onPriceLteChange}
        variant="label"
      />}
    </FilterPanelFilterGroup>
    {categoryItems.map(item =>
      <FilterPanelFilterGroup
        key={item.category.id}
        title={item.category.name}
        canReset={item.items.some(item => item.checked)}
        onReset={() => onResetCategory(item)}
      >
        <FilterPanelCheckboxGroup
          name="categories"
          items={item.items}
          onChange={onCategoryChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>,
    )}
    {collectionItems.length > 0 && (
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'ryMZI4XWwRxmXFr_aXfo',
          defaultMessage: 'Collections',
          description: 'Title for the collections filter group in experience search filters',
        })}
        canReset={searchFilters.collections && searchFilters.collections.length > 0}
        onReset={() => onResetCollection()}
      >
        <FilterPanelCheckboxGroup
          name="collections"
          items={collectionItems}
          onChange={onCollectionChange}
        />
      </FilterPanelFilterGroup>
    )}
  </VerticalSpacer>
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceSearchPageFilters)
